import { useRef, useCallback } from 'react'

const RECORDING_SERVER_URL = process.env.REACT_APP_RECORDING_SERVER_URL || 'wss://your-server.railway.app'

const VIDEO_TIMESLICE_MS = 5000

/**
 * useRecording — client-side audio mixing via Web Audio API
 *
 * Mixes candidate mic + AI agent audio in the browser using AudioContext,
 * combines with screen capture video, and streams as ONE WebSocket to Railway.
 * Simultaneously streams transcript entries over a second WebSocket.
 *
 * Stream layout:
 *   /ws/{roomId}/video       → binary webm chunks (screen + mic + AI audio mixed)
 *   /ws/{roomId}/transcript  → JSON lines  { role, text, ts }
 *
 * Railway re-encodes webm → mp4, saves transcript.json to Firebase Storage,
 * and writes transcriptUrl into rooms/{roomId}.
 *
 * Usage:
 *   const recording = useRecording()
 *   await recording.start(micStream, roomId)
 *   recording.sendAudioChunk(pcmBuffer)      ← tap from aiAgentService
 *   recording.sendTranscript(role, text)     ← tap from aiAgentService.onTranscript
 *   await recording.stop()
 */
export function useRecording() {
    // ── Video recording refs ─────────────────────────────────────────────────
    const mediaRecorderRef = useRef(null)
    const screenStreamRef = useRef(null)
    const socketRef = useRef(null)
    const audioCtxRef = useRef(null)
    const destinationRef = useRef(null)
    const isActiveRef = useRef(false)
    const nextPlayTimeRef = useRef(0)

    // ── Transcript refs ──────────────────────────────────────────────────────
    const transcriptSocketRef = useRef(null)
    const transcriptBufferRef = useRef([])

    // ── Gauge refs ───────────────────────────────────────────────────────────
    const gaugeSocketRef = useRef(null)
    const gaugeBufferRef = useRef([])
    const gaugeTimerRef = useRef(null)

    const sessionSocketRef = useRef(null)

    // ── Connect WebSocket and wait for READY ─────────────────────────────────

    const connectSocket = useCallback((url) => {
        return new Promise((resolve, reject) => {
            const ws = new WebSocket(url)
            ws.binaryType = 'arraybuffer'

            const timeout = setTimeout(() => {
                reject(new Error(`WebSocket timed out: ${url}`))
                ws.close()
            }, 10000)

            ws.onopen = () => console.log(`[Recording] WS connected: ${url}`)

            ws.onmessage = (event) => {
                const msg = typeof event.data === 'string' ? event.data : null
                if (msg === 'READY') {
                    clearTimeout(timeout)
                    resolve(ws)
                }
            }

            ws.onerror = (err) => {
                clearTimeout(timeout)
                console.error(`[Recording] WS error:`, err)
                reject(err)
            }

            ws.onclose = (event) => {
                clearTimeout(timeout)
                console.log(`[Recording] WS closed (code=${event.code})`)
            }
        })
    }, [])

    const sendIfOpen = useCallback((socket, data) => {
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(data)
        }
    }, [])

    /**
     * Drains transcriptBufferRef over the given socket.
     *
     * WebSocket.send() is non-blocking — it hands the data to the browser's
     * internal send queue and returns immediately. There is no benefit to
     * awaiting between sends, and doing so (even with Promise.resolve()) keeps
     * control in the microtask queue and can delay renders. We just drain the
     * whole buffer synchronously in one pass.
     *
     * Safe to call even if the buffer is empty.
     */
    const flushTranscriptBuffer = useCallback((socket) => {
        const buffer = transcriptBufferRef.current
        if (!buffer.length) return

        // Splice into a local copy first so any onTranscript calls that fire
        // during this loop go into a fresh buffer and aren't double-sent.
        const toSend = buffer.splice(0, buffer.length)
        console.log(`[Transcript] Flushing ${toSend.length} buffered entries`)

        for (let i = 0;i < toSend.length;i++) {
            if (socket.readyState !== WebSocket.OPEN) {
                console.warn('[Transcript] Socket closed mid-flush — re-buffering remaining entries')
                // Prepend un-sent tail back so stop() can pick them up
                transcriptBufferRef.current.unshift(...toSend.slice(i))
                return
            }
            try {
                socket.send(JSON.stringify(toSend[i]))
            } catch (err) {
                console.error('[Transcript] Flush send error:', err)
                transcriptBufferRef.current.unshift(...toSend.slice(i))
                return
            }
        }
    }, [])

    const flushGaugeBuffer = useCallback((socket) => {
        const buffer = gaugeBufferRef.current
        if (!buffer.length || !socket) return

        const toSend = buffer.splice(0, buffer.length)

        for (let i = 0;i < toSend.length;i++) {
            if (socket.readyState !== WebSocket.OPEN) {
                gaugeBufferRef.current.unshift(...toSend.slice(i))
                return
            }
            try {
                socket.send(JSON.stringify(toSend[i]))
            } catch (err) {
                console.error('[Gauge] Flush send error:', err)
                gaugeBufferRef.current.unshift(...toSend.slice(i))
                return
            }
        }
    }, [])


    const sendGauge = useCallback((frame) => {
        if (!frame) return

        const socket = gaugeSocketRef.current

        if (socket && socket.readyState === WebSocket.OPEN) {
            // Socket ready — goes straight into the 1s batch buffer
            gaugeBufferRef.current.push(frame)
        } else {
            // Not ready yet — buffer and flush once connected
            gaugeBufferRef.current.push(frame)
        }
    }, [])

    // ── START ────────────────────────────────────────────────────────────────

    const start = useCallback(async (micStream, roomId, existingScreenStream) => {
        if (isActiveRef.current) {
            console.warn('[Recording] Already active')
            return
        }

        isActiveRef.current = true
        console.log(`[Recording] Starting for room ${roomId}`)

        try {
            // ── 1. Connect all WebSockets in parallel ──────────────────────────
            const [socket, transcriptSocket, gaugeSocket, sessionSocket] = await Promise.all([
                connectSocket(`${RECORDING_SERVER_URL}/ws/${roomId}/video`),
                connectSocket(`${RECORDING_SERVER_URL}/ws/${roomId}/transcript`),
                connectSocket(`${RECORDING_SERVER_URL}/ws/${roomId}/gauge`),
                connectSocket(`${RECORDING_SERVER_URL}/ws/${roomId}/session`),
            ])

            socketRef.current = socket
            transcriptSocketRef.current = transcriptSocket
            gaugeSocketRef.current = gaugeSocket
            sessionSocketRef.current = sessionSocket

            console.log('[Recording] All three WebSockets connected')

            // Flush entries that arrived before the transcript socket was ready
            flushTranscriptBuffer(transcriptSocket)

            // Flush any gauge frames buffered before socket was ready
            flushGaugeBuffer(gaugeSocket)

            // Batch flush every 1000ms
            gaugeTimerRef.current = setInterval(() => {
                flushGaugeBuffer(gaugeSocketRef.current)
            }, 1000)

            // ── 2. Capture screen ───────────────────────────────────────────────
            let screenStream
            if (existingScreenStream && existingScreenStream.getVideoTracks().length > 0) {
                screenStream = existingScreenStream
                console.log('[Recording] Reusing existing screen stream')
            } else {
                try {
                    screenStream = await navigator.mediaDevices.getDisplayMedia({
                        video: {
                            displaySurface: 'monitor',
                            frameRate: { ideal: 30, max: 30 },
                            width: { ideal: 1920 },
                            height: { ideal: 1080 },
                        },
                        audio: false,
                    })
                } catch (err) {
                    console.warn('[Recording] Screen capture cancelled or denied:', err.message)
                    sendIfOpen(socket, 'END')
                    sendIfOpen(transcriptSocket, 'END')
                    isActiveRef.current = false
                    return
                }
            }

            // ── 3. Set up AudioContext mixing board ─────────────────────────────
            const audioCtx = new AudioContext({ sampleRate: 44100 })
            const destination = audioCtx.createMediaStreamDestination()

            // Wire candidate mic into the mix
            const micAudioTracks = micStream?.getAudioTracks() ?? []
            if (micAudioTracks.length > 0) {
                const micSource = audioCtx.createMediaStreamSource(
                    new MediaStream(micAudioTracks)
                )
                micSource.connect(destination)
                console.log('[Recording] Mic connected to mix')
            } else {
                console.warn('[Recording] No mic tracks found')
            }

            audioCtxRef.current = audioCtx
            destinationRef.current = destination
            nextPlayTimeRef.current = 0

            // ── 4. Build final stream: screen video + mixed audio ───────────────
            const combinedStream = new MediaStream([
                ...screenStream.getVideoTracks(),        // screen — no camera tile
                ...destination.stream.getAudioTracks(),  // mic + AI mixed
            ])

            console.log(`[Recording] Combined stream ready:
  video: ${screenStream.getVideoTracks().length} track(s) (screen)
  audio: ${destination.stream.getAudioTracks().length} track(s) (mic + AI mix)`)

            // ── 5. MediaRecorder → video WebSocket ──────────────────────────────
            const mimeType = getSupportedMimeType()
            console.log(`[Recording] MIME type: ${mimeType}`)

            const mediaRecorder = new MediaRecorder(combinedStream, {
                mimeType,
                videoBitsPerSecond: 1_500_000,  // 1.5 Mbps — ample for screen content
                audioBitsPerSecond: 128_000,    // 128 kbps mixed audio
            })

            mediaRecorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    sendIfOpen(socket, event.data)
                }
            }

            mediaRecorder.onerror = (err) => {
                console.error('[Recording] MediaRecorder error:', err)
            }

            mediaRecorderRef.current = mediaRecorder
            mediaRecorder.start(VIDEO_TIMESLICE_MS)
            console.log(`[Recording] MediaRecorder started (timeslice: ${VIDEO_TIMESLICE_MS}ms)`)

            // ── 6. Heartbeat on video socket ────────────────────────────────────
            const heartbeatInterval = setInterval(() => {
                sendIfOpen(socket, 'PING')
            }, 30_000)
            socket._heartbeatInterval = heartbeatInterval

        } catch (err) {
            console.error('[Recording] Failed to start:', err)
            isActiveRef.current = false
            // Never crash the interview
        }
    }, [connectSocket, sendIfOpen])

    // ── SEND AI AUDIO CHUNK ──────────────────────────────────────────────────

    /**
     * Called for every PCM chunk the AI agent plays through the speaker.
     * Decodes s16le PCM and feeds it into the AudioContext destination so
     * MediaRecorder captures it as part of the mixed audio track.
     *
     * Format: s16le PCM, 24000 Hz, mono (Deepgram TTS output format)
     */
    const sendAudioChunk = useCallback((arrayBuffer) => {
        const audioCtx = audioCtxRef.current
        const destination = destinationRef.current
        if (!audioCtx || !destination) return

        // s16le → float32
        const int16 = new Int16Array(arrayBuffer)
        const float32 = new Float32Array(int16.length)
        for (let i = 0;i < int16.length;i++) {
            float32[i] = int16[i] / 32768.0
        }

        // AudioBuffer at 24 kHz (AI TTS sample rate)
        const buffer = audioCtx.createBuffer(1, float32.length, 24000)
        buffer.copyToChannel(float32, 0)

        // Schedule sequentially so chunks don't overlap
        const source = audioCtx.createBufferSource()
        source.buffer = buffer
        source.connect(destination) // → mixed audio track captured by MediaRecorder

        const startTime = Math.max(audioCtx.currentTime, nextPlayTimeRef.current)
        source.start(startTime)
        nextPlayTimeRef.current = startTime + buffer.duration
    }, [])

    // ── SEND TRANSCRIPT ENTRY ────────────────────────────────────────────────

    /**
     * Called from aiAgentService.onTranscript(role, text).
     * Sends { role, text, ts } over the transcript WebSocket.
     * If the socket isn't ready yet, buffers the entry and flushes on connect.
     *
     * @param {'agent'|'candidate'} role
     * @param {string} text
     */
    const sendTranscript = useCallback((role, text, timestamps) => {
        if (!text?.trim()) return

        const entry = {
            role,
            text: text.trim(),
            ts: Date.now(),
            startTs: timestamps?.startTs ?? null,
            endTs: timestamps?.endTs ?? null,
        }
        const socket = transcriptSocketRef.current

        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(entry))
        } else {
            transcriptBufferRef.current.push(entry)
            console.log(`[Transcript] Buffered entry (socket not ready): [${role}] ${text.slice(0, 60)}`)
        }
    }, [])

    // ── STOP ────────────────────────────────────────────────────────────────

    const stop = useCallback(() => {
        return new Promise((resolve) => {
            if (!isActiveRef.current) {
                resolve()
                return
            }

            console.log('[Recording] Stopping...')
            isActiveRef.current = false

            const socket = socketRef.current
            const transcriptSocket = transcriptSocketRef.current

            // ── Clear heartbeat ─────────────────────────────────────────────────
            if (socket?._heartbeatInterval) {
                clearInterval(socket?._heartbeatInterval)
            }

            // ── Close transcript socket ─────────────────────────────────────────
            // Flush any remaining buffered entries BEFORE sending END
            if (transcriptSocket) {
                flushTranscriptBuffer(transcriptSocket)
                sendIfOpen(transcriptSocket, 'END')
                transcriptSocketRef.current = null
                console.log('[Transcript] END sent')
            }

            // ── Stop screen capture ─────────────────────────────────────────────
            if (screenStreamRef.current) {
                screenStreamRef.current.getTracks().forEach(t => t.stop())
                screenStreamRef.current = null
            }

            // ── Close AudioContext ──────────────────────────────────────────────
            if (audioCtxRef.current) {
                audioCtxRef.current.close().catch(() => { })
                audioCtxRef.current = null
                destinationRef.current = null
                nextPlayTimeRef.current = 0
            }

            // ── Clear gauge timer ───────────────────────────────────────────────
            if (gaugeTimerRef.current) {
                clearInterval(gaugeTimerRef.current)
                gaugeTimerRef.current = null
            }

            // ── Close gauge socket ──────────────────────────────────────────────
            const gaugeSocket = gaugeSocketRef.current
            if (gaugeSocket) {
                flushGaugeBuffer(gaugeSocket)
                sendIfOpen(gaugeSocket, 'END')
                gaugeSocketRef.current = null
                console.log('[Gauge] END sent')
            }

            const sessionSocket = sessionSocketRef.current
            if (sessionSocket) {
                sessionSocket.close()
                sessionSocketRef.current = null
                console.log('[Session] Closed — interview end marked')
            }

            // ── Stop MediaRecorder → send END on video socket ───────────────────
            const recorder = mediaRecorderRef.current

            const sendEnd = () => {
                if (socket?.readyState === WebSocket.OPEN) {
                    socket.send('END')
                }
                socketRef.current = null
                mediaRecorderRef.current = null
                console.log('[Recording] END sent — Railway will re-encode and upload')
                resolve()
            }

            if (recorder && recorder.state !== 'inactive') {
                // Final ondataavailable fires before onstop
                recorder.onstop = sendEnd
                recorder.stop()
            } else {
                sendEnd()
            }
        })
    }, [sendIfOpen, flushTranscriptBuffer])

    return { start, stop, sendAudioChunk, sendTranscript, sendGauge}
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getSupportedMimeType() {
    const candidates = [
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=h264,opus',
        'video/webm',
        'video/mp4',
    ]
    for (const type of candidates) {
        if (MediaRecorder.isTypeSupported(type)) return type
    }
    return ''
}